import React, { useState } from 'react';
import {
  Card, Button, Typography, Space, Divider, Row, Col, Alert, Upload, Image, message,
} from 'antd';
import type { UploadFile } from 'antd';
import { CloudUploadOutlined, PictureOutlined, InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import request from '@/utils/request';
import { buildImageUrl } from '@zero/shared';

const { Title, Text, Paragraph } = Typography;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_MB = 10;
const STORAGE_KEY = 'cos_image_test_result';
const EXPIRE_MS = 3600_000; // pre-signed URLs expire in ~1h

interface TestMetrics {
  urlGenTime: number;
  uploadTime: number;
  accessUrlTime: number;
  imageLoadTime: number;
  fileSize: number;
}

interface StoredTestResult {
  metrics: TestMetrics;
  originalUrl: string;
  thumbnailUrl: string;
  fileName: string;
  timestamp: number;
}

function loadStoredResult(): StoredTestResult | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredTestResult;
    if (Date.now() - data.timestamp > EXPIRE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

const StorageTestPage: React.FC = () => {
  const stored = loadStoredResult();

  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [testing, setTesting] = useState(false);
  const [metrics, setMetrics] = useState<TestMetrics | null>(stored?.metrics ?? null);
  const [originalUrl, setOriginalUrl] = useState(stored?.originalUrl ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState(stored?.thumbnailUrl ?? '');
  const [storedFileName, setStoredFileName] = useState(stored?.fileName ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleBeforeUpload = (f: File) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      message.error(`仅允许图片文件（${ALLOWED_TYPES.join(', ')}）`);
      return false;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      message.error(`文件大小需小于 ${MAX_SIZE_MB}MB`);
      return false;
    }
    setFile(f);
    setFileList([
      {
        uid: '-1',
        name: f.name,
        size: f.size,
        type: f.type,
        status: 'done',
        originFileObj: f as never,
      },
    ]);
    setMetrics(null);
    setOriginalUrl('');
    setThumbnailUrl('');
    setError(null);
    return false;
  };

  const handleTest = async () => {
    if (!file) {
      message.warning('请先选择一张图片');
      return;
    }
    setTesting(true);
    setMetrics(null);
    setOriginalUrl('');
    setThumbnailUrl('');
    setError(null);

    try {
      // 1. 申请预签名上传 URL
      const t1 = performance.now();
      const { data: urlData } = await request.post('/cos/upload-url', {
        filename: file.name,
        contentType: file.type,
      });
      const urlGenTime = Math.round(performance.now() - t1);
      if (!urlData?.uploadUrl) throw new Error('后端未返回 uploadUrl');

      // 2. 上传到 COS
      const t2 = performance.now();
      await axios.put(urlData.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        timeout: 120000,
      });
      const uploadTime = Math.round(performance.now() - t2);

      // 3. 换取访问 URL
      const t3 = performance.now();
      const { data: accessData } = await request.post('/cos/access-url', {
        cosKey: urlData.cosKey,
      });
      const accessUrlTime = Math.round(performance.now() - t3);
      const accessUrl = accessData.accessUrl;

      // 4. 测量浏览器加载时间
      const thumbUrl = buildImageUrl(accessUrl, { w: 200, h: 200 });
      const imageLoadTime = await new Promise<number>((resolve, reject) => {
        const img = new window.Image();
        const t4 = performance.now();
        img.onload = () => resolve(Math.round(performance.now() - t4));
        img.onerror = () => reject(new Error('浏览器加载 COS 图片失败'));
        img.src = thumbUrl;
      });

      const result: TestMetrics = {
        urlGenTime,
        uploadTime,
        accessUrlTime,
        imageLoadTime,
        fileSize: file.size,
      };
      setOriginalUrl(accessUrl);
      setThumbnailUrl(thumbUrl);
      setMetrics(result);
      setStoredFileName(file.name);

      const toStore: StoredTestResult = {
        metrics: result,
        originalUrl: accessUrl,
        thumbnailUrl: thumbUrl,
        fileName: file.name,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (err) {
      setError((err as Error)?.message || '测试失败');
    } finally {
      setTesting(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setFileList([]);
    setMetrics(null);
    setOriginalUrl('');
    setThumbnailUrl('');
    setStoredFileName('');
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        存储测试
      </Title>

      <Card
        title={
          <Space>
            <PictureOutlined />
            <span>COS 图片上传链路</span>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Upload.Dragger
                accept={ALLOWED_TYPES.join(',')}
                beforeUpload={handleBeforeUpload}
                fileList={fileList}
                onRemove={() => {
                  setFile(null);
                  setFileList([]);
                }}
                maxCount={1}
                style={{ padding: '8px 0' }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖入图片</p>
                <p className="ant-upload-hint">
                  支持 JPEG / PNG / WebP / GIF，最大 {MAX_SIZE_MB}MB
                </p>
              </Upload.Dragger>
            </Col>
          </Row>

          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={handleTest}
            loading={testing}
            disabled={!file}
          >
            上传并测试
          </Button>

          {error && <Alert type="error" showIcon message="测试失败" description={error} />}

          {metrics && (
            <>
              {!file && storedFileName && (
                <Alert
                  type="info"
                  showIcon
                  message={`上次测试结果：${storedFileName}（签名 URL 约 1 小时失效）`}
                />
              )}

              <Row gutter={16}>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      缩略图 (200x200)
                    </Text>
                    <Image
                      src={thumbnailUrl}
                      width={200}
                      height={200}
                      style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }}
                      preview={false}
                    />
                  </div>
                </Col>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      原图（点击预览）
                    </Text>
                    <Image
                      src={buildImageUrl(originalUrl, { w: 400 })}
                      width={200}
                      height={200}
                      style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }}
                      preview={{ src: originalUrl }}
                    />
                  </div>
                </Col>
              </Row>

              <div
                style={{
                  padding: 16,
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  background: '#fafafa',
                }}
              >
                <Space direction="vertical" size={4}>
                  <Text>
                    签名 URL 生成：<Text strong>{metrics.urlGenTime}ms</Text>
                  </Text>
                  <Text>
                    上传耗时：<Text strong>{metrics.uploadTime}ms</Text>（
                    {(metrics.fileSize / 1024 / 1024 / (metrics.uploadTime / 1000)).toFixed(2)} MB/s
                    ）
                  </Text>
                  <Text>
                    换取访问 URL：<Text strong>{metrics.accessUrlTime}ms</Text>
                  </Text>
                  <Text>
                    浏览器加载：<Text strong>{metrics.imageLoadTime}ms</Text>
                  </Text>
                  <Divider style={{ margin: '8px 0' }} />
                  <Text>
                    总计：
                    <Text strong type="success">
                      {metrics.urlGenTime +
                        metrics.uploadTime +
                        metrics.accessUrlTime +
                        metrics.imageLoadTime}
                      ms
                    </Text>
                  </Text>
                  <Text>
                    文件大小：<Text strong>{formatSize(metrics.fileSize)}</Text>
                  </Text>
                </Space>
              </div>

              <div
                style={{
                  padding: 16,
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  background: '#fafafa',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <div>
                    <Text type="secondary">原图 URL：</Text>
                    <Paragraph
                      copyable
                      style={{ margin: '4px 0 0', fontSize: 12, wordBreak: 'break-all' }}
                    >
                      {originalUrl}
                    </Paragraph>
                  </div>
                  <div>
                    <Text type="secondary">缩略图 URL：</Text>
                    <Paragraph
                      copyable
                      style={{ margin: '4px 0 0', fontSize: 12, wordBreak: 'break-all' }}
                    >
                      {thumbnailUrl}
                    </Paragraph>
                  </div>
                </Space>
              </div>

              <Button onClick={handleClear}>清空结果</Button>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default StorageTestPage;
